type JsPdfCtor = new (options?: {
  orientation?: string;
  unit?: string;
  format?: string;
}) => {
  internal: { pageSize: { getWidth: () => number; getHeight: () => number } };
  addImage: (
    imageData: string,
    format: string,
    x: number,
    y: number,
    w: number,
    h: number,
  ) => void;
  addPage: () => void;
  save: (filename: string) => void;
};

type Html2CanvasFn = (
  element: HTMLElement,
  options?: Record<string, unknown>,
) => Promise<HTMLCanvasElement>;

function loadScript(src: string, globalKey: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[data-pdf-lib="${globalKey}"]`);
    if (existing) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.dataset.pdfLib = globalKey;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${globalKey}`));
    document.head.appendChild(script);
  });
}

async function loadPdfLibs(): Promise<{ jsPDF: JsPdfCtor; html2Canvas: Html2CanvasFn }> {
  const w = window as unknown as {
    html2canvas?: Html2CanvasFn;
    jspdf?: { jsPDF: JsPdfCtor };
  };

  if (w.html2canvas && w.jspdf?.jsPDF) {
    return { jsPDF: w.jspdf.jsPDF, html2Canvas: w.html2canvas };
  }

  await loadScript(
    'https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js',
    'html2canvas',
  );
  await loadScript('https://cdn.jsdelivr.net/npm/jspdf@2.5.2/dist/jspdf.umd.min.js', 'jspdf');

  if (!w.html2canvas || !w.jspdf?.jsPDF) {
    throw new Error('PDF libraries failed to load');
  }
  return { jsPDF: w.jspdf.jsPDF, html2Canvas: w.html2canvas };
}

/** Render a full HTML document string to a downloaded PDF (A4). */
export async function downloadHtmlAsPdf(html: string, filename: string): Promise<void> {
  const { jsPDF, html2Canvas } = await loadPdfLibs();

  const styleMatch = html.match(/<style[^>]*>([\s\S]*?)<\/style>/i);
  const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  const styles = styleMatch?.[1] ?? '';
  const bodyHtml = bodyMatch?.[1] ?? html;

  const pageCssWidth = 794;
  const host = document.createElement('div');
  host.setAttribute('data-pdf-host', 'true');
  host.style.cssText = [
    'position:fixed',
    'left:-10000px',
    'top:0',
    `width:${pageCssWidth}px`,
    'background:#fff',
    'z-index:-1',
    'pointer-events:none',
    'overflow:visible',
  ].join(';');
  host.innerHTML = `<style>${styles}</style><div class="pdf-root">${bodyHtml}</div>`;
  document.body.appendChild(host);

  try {
    const root = host.querySelector('.pdf-root') as HTMLElement;
    root.style.cssText = 'width:100%;max-width:100%;overflow:visible;background:#fff;';

    const canvas = await html2Canvas(root, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      logging: false,
      width: pageCssWidth,
      windowWidth: pageCssWidth,
      scrollX: 0,
      scrollY: 0,
    });

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 12;
    const contentWidth = pageWidth - margin * 2;
    const contentHeight = pageHeight - margin * 2;

    const imgWidth = contentWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    const imgData = canvas.toDataURL('image/jpeg', 0.95);

    let heightLeft = imgHeight;
    let position = margin;

    pdf.addImage(imgData, 'JPEG', margin, position, imgWidth, imgHeight);
    heightLeft -= contentHeight;

    while (heightLeft > 0) {
      position = margin - (imgHeight - heightLeft);
      pdf.addPage();
      pdf.addImage(imgData, 'JPEG', margin, position, imgWidth, imgHeight);
      heightLeft -= contentHeight;
    }

    pdf.save(filename.endsWith('.pdf') ? filename : `${filename}.pdf`);
  } finally {
    host.remove();
  }
}
